import { AssetGroupResolver } from "../core/resolvers/SDKAssetGroupResolver";
import { ComponentGroupResolver } from "../core/resolvers/SDKComponentGroupResolver";
import { DesignComponentGroupResolver } from "../core/resolvers/SDKDesignComponentGroupResolver";
import { TokenGroupResolver } from "../core/resolvers/SDKTokenGroupResolver";
import { TokenResolver } from "../core/resolvers/SDKTokenResolver";
import { Asset } from "../model/assets/SDKAsset";
import { Brand } from "../model/base/SDKBrand";
import { DesignSystem } from "../model/base/SDKDesignSystem";
import { DesignSystemContactList } from "../model/base/SDKDesignSystemContactList";
import { DesignSystemMemberList } from "../model/base/SDKDesignSystemMemberList";
import { DesignSystemVersion, } from "../model/base/SDKDesignSystemVersion";
import { Component, } from "../model/components/SDKComponent";
import { DesignComponent, } from "../model/components/SDKDesignComponent";
import { DesignComponentVariant } from "../model/components/SDKDesignComponentVariant";
import { ElementDataView, } from "../model/elements/SDKElementDataView";
import { ElementProperty, } from "../model/elements/SDKElementProperty";
import { ElementPropertyValue, } from "../model/elements/values/SDKElementPropertyValue";
import { ExportBuild, } from "../model/exporters/exporter_builds/SDKExportBuild";
import { MCPStream } from "../model/mcp/SDKMCPStream";
import { Membership, } from "../model/membership/SDKMembership";
import { ChatThread } from "../model/messaging/SDKChatThread";
import { ChatThreadMessage } from "../model/messaging/SDKChatThreadMessage";
import { ForgeOnboardingContainerUsage, } from "../model/projects/SDKForgeOnboardingContainerUsage";
import { ForgeProject, } from "../model/projects/SDKForgeProject";
import { ForgeProjectContext, } from "../model/projects/SDKForgeProjectContext";
import { ForgeProjectContextOverride, } from "../model/projects/SDKForgeProjectContextOverride";
import { ForgeProjectFeature } from "../model/projects/SDKForgeProjectFeature";
import { ForgeProjectFeatureArtifactList } from "../model/projects/SDKForgeProjectFeatureArtifactList";
import { ForgeProjectFeatureIteration } from "../model/projects/SDKForgeProjectFeatureIteration";
import { ForgeProjectIterationTag } from "../model/projects/SDKForgeProjectIterationTag";
import { ForgeProjectFeatureSandbox } from "../model/projects/SDKForgeProjectSandbox";
import { PublishedFeature, } from "../model/projects/SDKPublishedFeature";
import { ForgeProjectMembersList } from "../model/projects/memberships/SDKForgeProjectMembersList";
import { ForgeProjectMembership } from "../model/projects/memberships/SDKForgeProjectMembership";
import { ResourceFont, } from "../model/resources/SDKResourceFont";
import { SandboxTemplate, } from "../model/sandboxes/SDKSandboxTemplate";
import { StorybookEntry, } from "../model/storybook/SDKStorybookEntry";
import { Source } from "../model/support/SDKSource";
import { ThreadMessageList } from "../model/threads/SDKThreadMessageList";
import { TrailEvent, } from "../model/trail-events/SDKTrailEvent";
import { User } from "../model/users/SDKUser";
import { UserMembership, } from "../model/users/SDKUserMembership";
import { Workspace, } from "../model/workspaces/SDKWorkspace";
import { WorkspaceIntegrations, } from "../model/workspaces/SDKWorkspaceIntegration";
import { WorkspaceInvitation, } from "../model/workspaces/SDKWorkspaceInvitation";
import { PortalSettings } from "../model/workspaces/SDKWorkspacePortalSettings";
import { SubscriptionProductFeatures, } from "../model/workspaces/SDKWorkspaceProductFeatures";
import { WorkspaceSSOSettings, } from "../model/workspaces/SDKWorkspaceSSOSettings";
import { WorkspaceSubscription, } from "../model/workspaces/SDKWorkspaceSubscription";
export class RawUtils {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Build objects out of raw data
    /** Builds components from raw data */
    static buildComponentsFromRaw(rawData, properties, propertyValues) {
        return rawData.map((component) => new Component(component, properties, propertyValues));
    }
    /** Build component groups */
    static buildComponentGroupsFromRaw(rawData, components) {
        const resolver = new ComponentGroupResolver(components);
        const groups = resolver.resolveGroupData(rawData);
        return groups;
    }
    /** Builds assets from raw data */
    static buildAssetsFromRaw(rawData, sources) {
        // Manage duplicates
        const assetNames = new Map();
        rawData.forEach((item) => {
            if (item.exportProperties.isAsset) {
                assetNames.set(item.meta.name.toLowerCase(), 0);
            }
        });
        // Transform only exportable designComponents into assets
        const assets = [];
        rawData.forEach((asset) => {
            const lowercasedName = asset.meta.name.toLowerCase();
            if (asset.exportProperties.isAsset) {
                // @ts-expect-error TS(2345): Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
                assets.push(new Asset(asset, assetNames.get(lowercasedName), sources));
                // Increase number of duplicates
                // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                assetNames.set(lowercasedName, assetNames.get(lowercasedName) + 1);
            }
        });
        return assets;
    }
    /** Build asset groups */
    static buildAssetGroupsFromRaw(rawData, assets) {
        const resolver = new AssetGroupResolver(assets);
        const result = resolver.resolveGroupData(rawData);
        return result;
    }
    /** Builds tokens from raw data */
    static buildTokensFromRaw(rawData, properties, propertyValues, propertyViews, tokenGroups, sources, designSystemId, versionId) {
        const resolver = new TokenResolver(designSystemId, versionId);
        const result = resolver.resolveTokenData(rawData, tokenGroups, properties, propertyViews, propertyValues, sources);
        return result;
    }
    /** Build token groups */
    static buildTokenGroupsFromRaw(rawData) {
        const resolver = new TokenGroupResolver();
        const result = resolver.resolveGroupData(rawData);
        return result;
    }
    /** Builds elements from raw data */
    static buildElementsFromRaw(rawData) {
        return {
            propertyValues: rawData.propertyValues.map((value) => new ElementPropertyValue(value)),
            propertyViews: rawData.propertyViews.map((view) => new ElementDataView(view)),
            propertyDefinitions: rawData.propertyDefinitions.map((definition) => new ElementProperty(definition)),
        };
    }
    /** Build sources */
    static buildSourcesFromRaw(rawData) {
        return rawData.map((source) => new Source(source));
    }
    /** Build font resources */
    static buildFontResourcesFromRaw(rawData) {
        return rawData.map((d) => new ResourceFont(d));
    }
    /** Builds memberships */
    static buildMembershipsFromRaw(rawData) {
        return rawData.map((membership) => new Membership(membership));
    }
    /** Builds workspace members */
    static buildWorkspaceMembersFromRaw(rawData) {
        return rawData.map((membership) => new UserMembership(membership));
    }
    /** Builds workspace invitations */
    static buildWorkspaceInvitationsFromRaw(rawData) {
        return rawData.map((invitation) => new WorkspaceInvitation(invitation));
    }
    /** Builds design systems */
    static buildDesignSystemsFromRaw(rawData) {
        return rawData.map((designSystem) => new DesignSystem(designSystem));
    }
    /** Builds versions */
    static buildVersionsFromRaw(rawData) {
        return rawData.map((version) => new DesignSystemVersion(version));
    }
    /** Builds design system members */
    static buildDesignSystemMembersFromRaw(rawData) {
        return new DesignSystemMemberList(rawData);
    }
    /** Builds brands */
    static buildBrandsFromRaw(rawData) {
        return rawData.map((brand) => new Brand(brand));
    }
    /** Builds user */
    static buildUserFromRaw(rawData) {
        return new User(rawData);
    }
    /** Builds design components */
    static buildDesignComponentsFromRaw(rawData, sources) {
        const components = [];
        // Map for better performance
        const componentVariantsMap = new Map();
        rawData.forEach((component) => {
            if (!component.parentComponentPersistentId) {
                components.push(new DesignComponent(component, sources));
                return;
            }
            componentVariantsMap.set(component.persistentId, new DesignComponentVariant(component, sources));
        });
        components.forEach((component) => {
            if (component.childrenPersistentIds) {
                // fill children by component variants if exists
                component.children = component.childrenPersistentIds.reduce((children, id) => {
                    const variant = componentVariantsMap.get(id);
                    if (variant) {
                        children.push(variant);
                    }
                    return children;
                }, []);
            }
        });
        return components;
    }
    /** Builds design component groups */
    static buildDesignComponentGroupsFromRaw(rawData, designComponents) {
        const resolver = new DesignComponentGroupResolver(designComponents);
        const result = resolver.resolveGroupData(rawData);
        return result;
    }
    static buildStorybookEntriesFromRaw(rawData) {
        return rawData.map((entry) => new StorybookEntry(entry));
    }
    static buildDocumentationBuildsFromRaw(rawData) {
        return rawData.map((build) => new ExportBuild(build));
    }
    static buildForgeProjectsFromRaw(rawData) {
        return rawData.map((project) => new ForgeProject(project));
    }
    static buildForgeProjectMembershipsFromRaw(rawData) {
        return rawData.map((membership) => new ForgeProjectMembership(membership));
    }
    static buildForgeProjectMembersFromRaw(rawData) {
        return new ForgeProjectMembersList(rawData);
    }
    /** Builds chat threads */
    static buildChatThreadsFromRaw(rawData) {
        return rawData.threads.map((thread) => new ChatThread(thread));
    }
    /** Builds chat thread messages */
    static buildChatThreadMessagesFromRaw(rawData) {
        return rawData.messages.map((message) => new ChatThreadMessage(message));
    }
    static buildPortalSettingsFromRaw(rawData) {
        return new PortalSettings(rawData);
    }
    /** Builds user memberships */
    static buildUserMembershipsFromRaw(rawData) {
        return rawData.map((membership) => new Membership(membership));
    }
    /** Builds workspace subscription */
    static buildWorkspaceSubscriptionFromRaw(rawData) {
        return new WorkspaceSubscription(rawData);
    }
    static buildWorkspaceIntegrationsFromRaw(rawData) {
        return new WorkspaceIntegrations(rawData);
    }
    static buildWorkspaceProductFeaturesFromRaw(rawData) {
        return new SubscriptionProductFeatures(rawData);
    }
    static buildDesignSystemContacts(rawData) {
        return new DesignSystemContactList(rawData);
    }
    static buildThreadMessagesFromRaw(rawData) {
        return new ThreadMessageList(rawData);
    }
    static buildForgeProjectFeatureIterationsFromRaw(rawData) {
        return rawData.map((iteration) => new ForgeProjectFeatureIteration(iteration));
    }
    static buildForgeProjectFeatureArtifactsFromRaw(rawData) {
        return new ForgeProjectFeatureArtifactList(rawData);
    }
    static buildForgeProjectIterationTagsFromRaw(rawData) {
        return rawData.tags.map((tag) => new ForgeProjectIterationTag(tag));
    }
    static buildForgeProjectFeatureSandboxesFromRaw(rawData) {
        return rawData.sandboxes.map((sandbox) => new ForgeProjectFeatureSandbox(sandbox));
    }
    static buildForgeProjectFeaturesFromRaw(rawData) {
        return rawData.map((d) => new ForgeProjectFeature(d));
    }
    static buildForgeProjectContextsFromRaw(rawData) {
        return rawData.map((data) => {
            return new ForgeProjectContext(data);
        });
    }
    static buildForgeProjectContextOverrideFromRaw(rawData) {
        return new ForgeProjectContextOverride(rawData);
    }
    static buildForgeOnboardingContainerUsageFromRaw(rawData) {
        return new ForgeOnboardingContainerUsage(rawData);
    }
    static buildForgeProjectTrailEventsFromRaw(rawData) {
        return rawData.events.map((data) => new TrailEvent(data));
    }
    static buildPublishedFeatureFromRaw(rawData) {
        return new PublishedFeature(rawData);
    }
    static buildForgeProjectFromRaw(rawData) {
        return new ForgeProject(rawData);
    }
    static buildWorkspaceFromRaw(rawData) {
        return new Workspace(rawData);
    }
    static buildMCPStreamFromRaw(rawData) {
        return new MCPStream(rawData.stream);
    }
    static buildSanboxTemplatesFromRaw(rawData) {
        return rawData.map((data) => new SandboxTemplate(data));
    }
    static buildWorkspaceSSOSettingsFromRaw(rawData) {
        return new WorkspaceSSOSettings(rawData);
    }
}
